import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { InfoIcon, TargetIcon, EyeIcon, AwardIcon, ShieldCheckIcon, StarIcon, LightbulbIcon, HeartIcon, CheckCircle2Icon } from 'lucide-react';
export function AboutPage() {
  useScrollReveal();
  return <main className="w-full" data-id="element-128">
      <PageHero title="About AUDIOTECH" subtitle="Botswana's trusted partner in professional audio-visual and event production solutions since 2004." icon={InfoIcon} data-id="element-129" />

      {/* Company Story */}
      <section className="py-20 px-6 bg-brand-charcoal" data-id="element-130">
        <div className="max-w-7xl mx-auto" data-id="element-131">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-132">
            <div className="reveal" data-id="element-133">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-134">
                Our Story
              </h2>
              <div className="space-y-6 text-brand-muted-light text-lg leading-relaxed" data-id="element-135">
                <p data-id="element-136">
                  AUDIOTECH is a 100% Botswana citizen-owned company established
                  in 2004. Since its founding, the company has served a broad
                  range of clients with practical and professional technical
                  solutions in sound, staging, lighting, visuals, and event
                  production support.
                </p>
                <p data-id="element-137">
                  Our work is rooted in reliability, quality service, and a
                  commitment to delivering solutions that meet the demands of
                  each environment. Over two decades, we have grown from a small
                  technical provider to a comprehensive event production partner
                  trusted by government institutions, corporate entities, and
                  private clients across the nation.
                </p>
              </div>
            </div>
            <div className="reveal" style={{
            transitionDelay: '200ms'
          }} data-id="element-138">
              <div className="img-placeholder aspect-video w-full shadow-glow" data-id="element-139">
                <AwardIcon className="w-16 h-16 text-white/20" data-id="element-140" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-6 bg-brand-black relative" data-id="element-141">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-142">
          <div className="glass-card p-10 reveal" data-id="element-143">
            <div className="w-14 h-14 rounded-xl bg-brand-blue/10 flex items-center justify-center mb-6 text-brand-blue" data-id="element-144">
              <TargetIcon className="w-7 h-7" data-id="element-145" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4" data-id="element-146">Our Mission</h3>
            <p className="text-brand-muted-light text-lg leading-relaxed" data-id="element-147">
              To provide dependable, high-quality audio-visual and event
              production solutions that help our clients communicate, engage
              audiences, and execute memorable events with confidence.
            </p>
          </div>

          <div className="glass-card p-10 reveal" style={{
          transitionDelay: '200ms'
        }} data-id="element-148">
            <div className="w-14 h-14 rounded-xl bg-brand-cyan/10 flex items-center justify-center mb-6 text-brand-cyan" data-id="element-149">
              <EyeIcon className="w-7 h-7" data-id="element-150" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4" data-id="element-151">Our Vision</h3>
            <p className="text-brand-muted-light text-lg leading-relaxed" data-id="element-152">
              To be a leading Botswana brand in professional audio-visual,
              staging, and event production solutions, recognized for
              reliability, technical excellence, and service.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-6 bg-brand-charcoal" data-id="element-153">
        <div className="max-w-7xl mx-auto" data-id="element-154">
          <div className="text-center mb-16 reveal" data-id="element-155">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-156">
              Our Core Values
            </h2>
            <p className="text-brand-muted-light text-lg" data-id="element-157">
              The principles that guide every project we undertake.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-158">
            {[{
            icon: StarIcon,
            title: 'Professionalism'
          }, {
            icon: ShieldCheckIcon,
            title: 'Reliability'
          }, {
            icon: AwardIcon,
            title: 'Quality'
          }, {
            icon: LightbulbIcon,
            title: 'Innovation'
          }, {
            icon: HeartIcon,
            title: 'Client Commitment'
          }].map((value, i) => <div key={i} className="glass-card p-8 text-center reveal" style={{
            transitionDelay: `${i * 100}ms`
          }} data-id="element-159">
                <value.icon className="w-10 h-10 text-brand-blue mx-auto mb-4" data-id="element-160" />
                <h4 className="text-white font-bold" data-id="element-161">{value.title}</h4>
              </div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 bg-brand-black" data-id="element-162">
        <div className="max-w-4xl mx-auto reveal" data-id="element-163">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 text-center" data-id="element-164">
            Why Clients Choose AUDIOTECH
          </h2>
          <div className="space-y-6" data-id="element-165">
            {['100% Citizen-Owned with deep understanding of the local market', 'Over 20 years of proven industry experience', 'Comprehensive, end-to-end technical solutions', 'Premium, industry-standard equipment inventory', 'Highly trained and professional technical staff', 'Commitment to safety and flawless execution'].map((point, i) => <div key={i} className="flex items-center gap-4 glass-card p-6" data-id="element-166">
                <CheckCircle2Icon className="w-6 h-6 text-brand-cyan shrink-0" data-id="element-167" />
                <span className="text-white text-lg" data-id="element-168">{point}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-brand-charcoal text-center reveal" data-id="element-169">
        <div className="max-w-3xl mx-auto" data-id="element-170">
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-171">
            Ready to work with us?
          </h2>
          <p className="text-brand-muted-light mb-8 text-lg" data-id="element-172">
            Our team is ready to bring technical excellence to your next event.
          </p>
          <Link to="/contact" className="btn-glow" data-id="element-173">
            Contact Our Team
          </Link>
        </div>
      </section>
    </main>;
}